import {
  Box,
  HStack,
  RangeSlider,
  RangeSliderFilledTrack,
  RangeSliderMark,
  RangeSliderThumb,
  RangeSliderTrack,
  Stack,
  Text,
} from '@chakra-ui/react';
import { ChangeEvent, KeyboardEvent, useCallback, useState } from 'react';

const THUMB_SIZE = 16;

type SimpleRangeSliderProps<TValue> = {
  label: string;
  minValue: TValue;
  maxValue: TValue;
  value?: [TValue | null, TValue | null];
  onChange?: (range: [TValue | null, TValue | null]) => void;
  toIndex: (value: TValue) => number;
  fromIndex: (index: number) => TValue;
  formatValue: (value: TValue) => string;
  isEqual: (left: TValue, right: TValue) => boolean;
  anyLabel?: string;
  name?: string;
  onBlur?: (event: ChangeEvent<HTMLInputElement>) => void;
};

type RangeSliderFilterProps<TValue> = SimpleRangeSliderProps<TValue> & { anyLabel: string };

type DragThumb = 'min' | 'max';

const RangeSliderFilter = <TValue,>({
  label,
  minValue,
  maxValue,
  value: valueProp,
  onChange,
  toIndex,
  fromIndex,
  formatValue,
  isEqual,
  anyLabel,
  name,
  onBlur,
}: RangeSliderFilterProps<TValue>) => {
  const minIndexValue = toIndex(minValue);
  const maxIndexValue = toIndex(maxValue);
  const totalSteps = Math.max(maxIndexValue - minIndexValue, 1);
  const [hovering, setHovering] = useState<DragThumb | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const isControlled = valueProp !== undefined;
  const [internalValue, setInternalValue] = useState<[TValue | null, TValue | null]>([null, null]);
  const committedValue = isControlled ? valueProp : internalValue;

  const triggerBlur = useCallback(
    (nextRange: [TValue | null, TValue | null]) => {
      if (!name || !onBlur) return;

      onBlur({
        target: {
          name,
          value: JSON.stringify(nextRange),
        },
      } as ChangeEvent<HTMLInputElement>);
    },
    [name, onBlur],
  );

  const commitChange = useCallback(
    (range: [TValue | null, TValue | null]) => {
      const [nextMin, nextMax] = range;
      const isFullRange =
        nextMin !== null && nextMax !== null && isEqual(nextMin, minValue) && isEqual(nextMax, maxValue);
      const normalized: [TValue | null, TValue | null] = isFullRange ? [null, null] : range;
      if (!isControlled) setInternalValue(normalized);
      onChange?.(normalized);
      triggerBlur(normalized);
    },
    [isControlled, isEqual, maxValue, minValue, onChange, triggerBlur],
  );

  const handleClear = useCallback(() => {
    commitChange([null, null]);
  }, [commitChange]);

  const handleClearKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleClear();
      }
    },
    [handleClear],
  );

  const [minVal, maxVal] = committedValue ?? [null, null];
  const [previewIndexes, setPreviewIndexes] = useState<[number, number] | null>(null);

  const committedIndexes: [number, number] = [
    minVal ? toIndex(minVal) - minIndexValue : 0,
    maxVal ? toIndex(maxVal) - minIndexValue : maxIndexValue - minIndexValue,
  ];
  const displayIndexes = previewIndexes ?? committedIndexes;
  const [minIndex, maxIndex] = displayIndexes;

  const toRangeFromIndexes = useCallback(
    (nextIndexes: number[]): [TValue | null, TValue | null] => {
      const [rawMin = 0, rawMax = totalSteps] = nextIndexes;
      const boundedMin = Math.max(0, Math.min(rawMin, totalSteps));
      const boundedMax = Math.max(boundedMin, Math.min(rawMax, totalSteps));
      return [fromIndex(boundedMin + minIndexValue), fromIndex(boundedMax + minIndexValue)];
    },
    [fromIndex, minIndexValue, totalSteps],
  );

  const handleRangeChange = useCallback(
    (nextIndexes: number[]) => {
      const [rawMin = 0, rawMax = totalSteps] = nextIndexes;
      const boundedMin = Math.max(0, Math.min(rawMin, totalSteps));
      const boundedMax = Math.max(boundedMin, Math.min(rawMax, totalSteps));
      setPreviewIndexes([boundedMin, boundedMax]);
    },
    [totalSteps],
  );

  const handleRangeChangeEnd = useCallback(
    (nextIndexes: number[]) => {
      setIsDragging(false);
      setPreviewIndexes(null);
      commitChange(toRangeFromIndexes(nextIndexes));
    },
    [commitChange, toRangeFromIndexes],
  );

  const hasValue = minVal !== null || maxVal !== null;

  const headerText = hasValue
    ? `${formatValue(minVal ?? minValue).toUpperCase()} - ${formatValue(maxVal ?? maxValue).toUpperCase()}`
    : anyLabel;

  const renderHandleTooltip = (thumb: DragThumb, index: number) => (
    <RangeSliderMark
      value={index}
      mt={-12}
      ml={2}
      zIndex={20}
      display={hovering === thumb || isDragging ? 'block' : 'none'}
      pointerEvents="none"
    >
      <Box position="relative" display="inline-block" transform="translateX(-50%)">
        <Box
          bg="gray.800"
          color="white"
          px={2.5}
          py={1.5}
          borderRadius="md"
          fontSize="xs"
          fontWeight="semibold"
          whiteSpace="nowrap"
          boxShadow="shadows.base"
        >
          {formatValue(fromIndex(index + minIndexValue))}
        </Box>
        <Box
          position="absolute"
          left="50%"
          bottom="-5px"
          transform="translateX(-50%)"
          w={0}
          h={0}
          borderLeft="6px solid transparent"
          borderRight="6px solid transparent"
          borderTop="6px solid"
          borderTopColor="gray.800"
          aria-hidden
        />
      </Box>
    </RangeSliderMark>
  );

  const renderRangeSliderThumb = (thumb: DragThumb, sliderIndex: 0 | 1) => (
    <RangeSliderThumb
      index={sliderIndex}
      boxSize={`${THUMB_SIZE}px`}
      bg="white"
      borderWidth={1}
      borderColor="border.default"
      borderRadius="full"
      boxShadow="shadows.base"
      _hover={{ borderColor: 'border.default', boxShadow: 'shadows.base' }}
      _focusVisible={{ boxShadow: 'shadows.base' }}
      onMouseEnter={() => setHovering(thumb)}
      onMouseLeave={() => setHovering(null)}
    >
      <Box />
    </RangeSliderThumb>
  );

  return (
    <Stack
      spacing={2}
      px={4}
      py={2}
      borderWidth={1}
      borderColor="border.default"
      borderRadius="md"
      bg="bg.surface"
      userSelect="none"
      zIndex={19}
    >
      <HStack justify="space-between" align="center" minW="60" spacing={3}>
        <HStack spacing={2} align="center" flex={1} minW={0}>
          <Text
            textStyle="label"
            color="text.default"
            fontWeight="bold"
            textTransform="uppercase"
            letterSpacing={0.05}
            flexShrink={0}
          >
            {label}
          </Text>
          <Text as="span" color="text.subtle" fontSize="xs" aria-hidden>
            •
          </Text>
          <Text
            textStyle="label"
            textTransform="uppercase"
            letterSpacing={0.04}
            color={hasValue ? 'primary.500' : 'text.subtle'}
            fontWeight={hasValue ? 'semibold' : 'normal'}
            noOfLines={1}
          >
            {headerText}
          </Text>
        </HStack>
        {hasValue && (
          <Text
            textStyle="label"
            color="primary.500"
            fontWeight="bold"
            textTransform="uppercase"
            letterSpacing={0.06}
            flexShrink={0}
            onClick={handleClear}
            cursor="pointer"
            role="button"
            tabIndex={0}
            aria-label="Clear date range"
            onKeyDown={handleClearKeyDown}
            _hover={{ textDecoration: 'underline' }}
          >
            CLEAR
          </Text>
        )}
      </HStack>

      <RangeSlider
        min={0}
        max={totalSteps}
        step={1}
        value={displayIndexes}
        name={name}
        onChange={handleRangeChange}
        onChangeStart={() => setIsDragging(true)}
        onChangeEnd={handleRangeChangeEnd}
        pt={1}
        pb={2}
        w={`calc(100% - ${THUMB_SIZE}px)`}
      >
        <RangeSliderTrack
          h={1}
          bg="border.default"
          borderRadius="full"
          overflow="visible"
          position="relative"
          left={`${THUMB_SIZE / 2}px`}
          w={`calc(100% - ${THUMB_SIZE}px)`}
        >
          <RangeSliderFilledTrack bg="primary.500" borderRadius="full" />
        </RangeSliderTrack>

        {renderHandleTooltip('min', minIndex)}
        {renderHandleTooltip('max', maxIndex)}

        {renderRangeSliderThumb('min', 0)}
        {renderRangeSliderThumb('max', 1)}
      </RangeSlider>
    </Stack>
  );
};

export const SimpleRangeSlider = <TValue,>({
  label,
  minValue,
  maxValue,
  value: valueProp,
  onChange,
  toIndex,
  fromIndex,
  formatValue,
  isEqual,
  anyLabel = 'ANY',
  name,
  onBlur,
}: SimpleRangeSliderProps<TValue>) => {
  return (
    <RangeSliderFilter<TValue>
      label={label}
      minValue={minValue}
      maxValue={maxValue}
      value={valueProp}
      onChange={onChange}
      toIndex={toIndex}
      fromIndex={fromIndex}
      formatValue={formatValue}
      isEqual={isEqual}
      anyLabel={anyLabel}
      name={name}
      onBlur={onBlur}
    />
  );
};
